import { Link } from "react-router-dom";
import type { Article, Column } from "@/mocks/news";

interface ArticleCardProps {
  article: Article;
  column: Column;
  rank?: number;
  compact?: boolean;
}

export function ArticleCard({ article, column, rank, compact }: ArticleCardProps) {
  return (
    <Link
      to={`/story/${article.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-100 transition-all hover:-translate-y-1 hover:border-secondary-300 hover:bg-background-50"
    >
      <div className="relative aspect-[3/2] w-full overflow-hidden">
        <img
          src={article.image}
          alt={`${article.title} - ${column.name}`}
          title={`${article.title} ${column.name}`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {rank && (
          <span
            className="absolute left-2 top-2 flex h-8 w-8 items-center justify-center rounded-lg font-heading text-lg font-black text-background-50"
            style={{ backgroundColor: column.accent }}
          >
            {rank}
          </span>
        )}
        <span
          className="absolute bottom-2 left-2 rounded-md px-2 py-0.5 text-xs font-bold text-background-50"
          style={{ backgroundColor: column.accent }}
        >
          {article.tag}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="line-clamp-2 text-sm font-bold leading-snug text-foreground-950 group-hover:text-primary-600 md:text-base">
          {article.title}
        </h3>
        {!compact && (
          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
            {article.excerpt}
          </p>
        )}
        <div className="mt-auto flex items-center gap-2 pt-3 text-[11px] text-foreground-500 md:text-xs">
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {article.date}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.views}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-book-open-line"></i>
            {article.readMin} 分钟
          </span>
        </div>
      </div>
    </Link>
  );
}

export function ArticleRow({ article, column }: { article: Article; column: Column }) {
  return (
    <Link
      to={`/story/${article.id}`}
      className="group flex items-start gap-3 border-b border-background-200 py-3 last:border-0"
    >
      <span
        className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: column.accent }}
      ></span>
      <div className="min-w-0 flex-1">
        <h4 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
          {article.title}
        </h4>
        <span className="mt-1 block text-[11px] text-foreground-400">{article.date}</span>
      </div>
      {article.hot && (
        <span className="flex shrink-0 items-center gap-1 rounded-md bg-secondary-100 px-1.5 py-0.5 text-[11px] font-bold text-secondary-800">
          <i className="ri-fire-fill text-accent-600"></i>
          {article.hot}
        </span>
      )}
    </Link>
  );
}